import React, { useState, useEffect, useMemo } from 'react';
import Header from '../Header';
import '../Header.css';

/**
 * Layout 우회 컴포넌트
 * LearningStatus 방식의 Layout 우회를 템플릿화한 컴포넌트
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - 렌더링할 컨텐츠
 * @param {boolean} props.loading - 로딩 상태
 * @param {string} props.className - 추가 CSS 클래스
 * @param {Object} props.style - 추가 인라인 스타일
 * @param {boolean} props.showSidebar - 데스크톱에서 사이드바 표시 여부 (기본: true)
 * @param {boolean} props.showMobileNav - 모바일에서 하단 네비게이션 표시 여부 (기본: true)
 * @param {Function} props.renderContent - 컨텐츠 렌더링 함수 (children 대신 사용 가능)
 */
const BypassLayout = ({ 
  children, 
  loading = false,
  className = '',
  style = {},
  showSidebar = true,
  showMobileNav = true,
  renderContent,
  extendViewportToNav = false // 네비게이션바까지 뷰포트 확장 옵션
}) => {
  // Layout 컴포넌트와 동일한 기준점으로 초기화
  const [isMobileView, setIsMobileView] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });
  const [safeAreaBottom, setSafeAreaBottom] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  // 디바이스 정보 감지
  const deviceInfo = useMemo(() => {
    if (typeof window === 'undefined') return { isIOS: false, isIPhone: false, isSafari: false };
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isIPhone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    return { isIOS, isIPhone, isSafari };
  }, []);

  useEffect(() => {
    const checkMobileView = () => {
      const newIsMobileView = window.innerWidth < 768;
      setIsMobileView(newIsMobileView);
    };

    const updateSafeArea = () => {
      // 실제 CSS env() 값을 직접 읽기
      const testEl = document.createElement('div');
      testEl.style.paddingBottom = 'env(safe-area-inset-bottom)';
      document.body.appendChild(testEl);
      const computedStyle = window.getComputedStyle(testEl);
      const actualSafeArea = parseInt(computedStyle.paddingBottom) || 0;
      document.body.removeChild(testEl);
      
      // iOS Safari의 동적 Safe Area 처리
      if (deviceInfo.isIOS && deviceInfo.isSafari) {
        // 실제 safe area 값이 15px 미만이면 0으로 처리 (주소창 보이는 상태)
        const dynamicSafeArea = actualSafeArea < 15 ? 0 : actualSafeArea;
        setSafeAreaBottom(dynamicSafeArea);
      } else {
        // 다른 브라우저는 실제 값 사용
        setSafeAreaBottom(actualSafeArea);
      }
    };

    const debouncedUpdate = () => {
      setTimeout(() => {
        checkMobileView();
        updateSafeArea();
        setIsInitialized(true);
      }, 100);
    };

    checkMobileView();
    updateSafeArea();
    setIsInitialized(true);
    
    window.addEventListener('resize', debouncedUpdate);
    window.addEventListener('orientationchange', debouncedUpdate);
    window.addEventListener('scroll', debouncedUpdate); // iOS Safari 주소창 토글 감지
    
    // visualViewport API 사용 (지원하는 경우)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', debouncedUpdate);
    }

    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('orientationchange', debouncedUpdate);
      window.removeEventListener('scroll', debouncedUpdate);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', debouncedUpdate);
      }
    };
  }, [deviceInfo.isIOS, deviceInfo.isSafari]);

  // 모바일 컨테이너 높이 계산 함수 (Layout과 동일한 방식)
  const getMobileContainerHeight = () => {
    if (!isMobileView) return 'auto';
    
    // extendViewportToNav가 true면 네비게이션바 영역까지 확장
    if (extendViewportToNav) {
      const contentHeight = `calc(100vh - 50px)`;
      const contentHeightDynamic = `calc(100dvh - 50px)`;
      return contentHeightDynamic;
    }
    
    // 기본: Layout과 동일한 방식으로 Safe Area를 높이 계산에 포함
    const totalBottomHeight = 68 + safeAreaBottom;
    const contentHeight = `calc(100vh - 50px - ${totalBottomHeight}px)`;
    const contentHeightDynamic = `calc(100dvh - 50px - ${totalBottomHeight}px)`;
    
    // Dynamic viewport height 지원
    return contentHeightDynamic;
  };

  // 모바일 패널 높이 계산 함수
  const getMobilePanelHeight = () => {
    if (!isMobileView) return 'auto';
    
    // 컨테이너 높이에서 패딩 제외
    return `calc(${getMobileContainerHeight()} - 2rem)`;
  };

  // 메인 컨텐츠 스타일 (Layout과 동일한 방식)
  const mainStyle = useMemo(() => {
    const baseStyle = {
      ...style
    };

    if (isMobileView) {
      const containerHeight = getMobileContainerHeight();
      return {
        ...baseStyle,
        // Layout과 동일한 모바일 스타일
        height: containerHeight,
        maxHeight: containerHeight,
        minHeight: containerHeight,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        // transform: 'translateZ(0)', // stacking context 생성 방지를 위해 제거
        // willChange: 'scroll-position', // stacking context 생성 방지를 위해 제거
        position: 'relative',
        boxSizing: 'border-box',
        // 패딩 제거 - renderContent에서 처리
        padding: '0'
      };
    }

    // 데스크톱에서는 기존대로 패딩 적용
    return {
      ...baseStyle,
      padding: '1rem',
      boxSizing: 'border-box'
    };
  }, [isMobileView, style, getMobileContainerHeight]);

  // 모바일 컨테이너 스타일 (Layout과 동일 - 100dvh 적용)
  const containerStyle = useMemo(() => {
    if (!isMobileView) return {};
    return {
      height: '100vh',
      height: '100dvh',
      maxHeight: '100vh',
      maxHeight: '100dvh',
      position: 'relative',
    };
  }, [isMobileView]);

  // 로딩 상태 렌더링
  if (loading) {
    return (
      <div className="flex h-screen flex-col" style={containerStyle}>
        <Header />
        <div className="header-spacer" style={{ height: '50px' }} />
        <div className="flex flex-1 overflow-hidden">
          <main className="flex flex-1 items-center justify-center bg-gray-100 p-4">
            <div className="flex flex-col items-center">
              <div className="size-12 animate-spin rounded-full border-b-2 border-primary"></div>
              <p className="mt-4 text-gray-600">로딩 중...</p>
            </div>
          </main>
        </div>
        {/* 모바일 하단 네비게이션바는 App.js에서 단일 인스턴스로 렌더링 */}
      </div>
    );
  }

  // 메인 컨텐츠 렌더링
  const content = renderContent ? renderContent({ 
    isMobileView, 
    safeAreaBottom, 
    isInitialized,
    deviceInfo,
    getMobileContainerHeight,
    getMobilePanelHeight,
    // Layout과 동일한 Safe Area 패딩 스타일 (고정 패딩만)
    getSafeAreaStyle: () => ({
      padding: isMobileView ? '14.4px' : '1rem',
      paddingTop: isMobileView ? '14.4px' : '1rem', // Safe Area는 높이 계산에서 처리하므로 고정 패딩
      paddingBottom: isMobileView ? '14.4px' : '1rem' // Safe Area는 높이 계산에서 처리하므로 고정 패딩
    }),
    // 네비게이션바와의 여백을 포함한 하단 패딩 (area3용)
    getBottomPaddingWithNav: () => ({
      paddingBottom: extendViewportToNav 
        ? `calc(1rem + 68px + ${safeAreaBottom}px)` // 확장 모드: 네비게이션바 높이 + Safe Area 포함
        : '1rem' // 기본 모드: 기본 패딩만
    })
  }) : children;

  return (
    <div className="flex h-screen flex-col" style={containerStyle}>
      <Header />
      <div className="header-spacer" style={{ height: '50px' }} />
      <div className="flex flex-1 overflow-hidden">
        <main
          className={`flex-1 min-w-0 bg-gray-100 ${isMobileView ? 'flex flex-col overflow-hidden' : ''} ${className}`}
          style={mainStyle}
        >
          {content}
        </main>
      </div>

      {/* 모바일 하단 네비게이션바는 App.js에서 단일 인스턴스로 렌더링 */}
    </div>
  );
};

export default BypassLayout;
